// 全局告警：监听 /ws alert → 横幅（扫码橙闪 / 审核紫 / 卡住红）+ 响铃 + 浏览器通知；完成 → 绿色 toast
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { CONFLICT_TEXT } from "./api";
import { useAuth } from "./auth";
import { addBanner, onTaskStatus, removeBanner, ringKind, type Banner } from "./alertState";
import { useWsMessages } from "./realtime";
import { audioUnlocked, startRinging, stopRinging, unlockAudio } from "./sound";
import type { AlertLevel } from "./types";

export interface Toast {
  id: string;
  kind: "ok" | "info" | "error";
  text: string;
  taskId?: string;
  /** toast 右侧的动作按钮（如 409 冲突时的「刷新」） */
  action?: ToastAction;
}

export interface ToastAction {
  label: string;
  run: () => void;
}

/** toast 停留时长（规范 §3.7：3.5 秒；出错和带动作的停久一点，留时间读和点） */
const TOAST_MS = { normal: 3500, error: 8000, action: 10000 };

interface AlertsApi {
  banners: Banner[];
  toasts: Toast[];
  openBanner: (b: Banner) => void;
  dismissBanner: (id: string) => void;
  toast: (kind: Toast["kind"], text: string, taskId?: string, action?: ToastAction) => void;
  /** 写操作遇到版本冲突（409）：提示「数据已更新，请刷新后重试」并给「刷新」按钮 */
  conflict: (onRefresh: () => void) => void;
  dismissToast: (id: string) => void;
  /** 提醒是否已开启（通知权限 + 声音解锁） */
  enabled: boolean;
  enable: () => Promise<void>;
}

const Ctx = createContext<AlertsApi | null>(null);
let seq = 0;
const uid = () => `${Date.now()}-${++seq}`;

function notificationGranted(): boolean {
  return typeof Notification !== "undefined" && Notification.permission === "granted";
}

const TITLES: Partial<Record<AlertLevel, string>> = {
  scan: "请扫码登录 Singpass",
  review: "有任务等待审核",
  blocked: "任务卡住了",
  failed: "任务失败",
};

export function AlertsProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [banners, setBanners] = useState<Banner[]>([]);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [enabled, setEnabled] = useState<boolean>(notificationGranted() && audioUnlocked());

  const toast = useCallback((kind: Toast["kind"], text: string, taskId?: string, action?: ToastAction) => {
    const id = uid();
    setToasts((l) => [...l, { id, kind, text, taskId, action }].slice(-5));
    const ms = action ? TOAST_MS.action : kind === "error" ? TOAST_MS.error : TOAST_MS.normal;
    setTimeout(() => setToasts((l) => l.filter((t) => t.id !== id)), ms);
  }, []);
  const conflict = useCallback(
    (onRefresh: () => void) => toast("error", CONFLICT_TEXT, undefined, { label: "刷新", run: onRefresh }),
    [toast],
  );

  const dismissToast = useCallback((id: string) => setToasts((l) => l.filter((t) => t.id !== id)), []);
  const dismissBanner = useCallback((id: string) => setBanners((l) => removeBanner(l, id)), []);
  const openBanner = useCallback(
    (b: Banner) => {
      setBanners((l) => removeBanner(l, b.id));
      navigate(`/tasks/${b.taskId}`);
    },
    [navigate],
  );

  useWsMessages((m) => {
    if (m.type === "task") {
      setBanners((l) => onTaskStatus(l, m.taskId, m.status));
      return;
    }
    if (m.type !== "alert") return;
    if (m.level === "done") {
      toast("ok", m.text, m.taskId);
      return;
    }
    if (m.level === "info") {
      toast("info", m.text, m.taskId);
      return;
    }
    // 老板审的检查点：文员只看提示，不弹横幅
    if (m.level === "review" && m.reviewer === "boss" && user?.role === "clerk") {
      toast("info", m.text, m.taskId);
      return;
    }
    setBanners((l) => addBanner(l, { id: uid(), level: m.level, taskId: m.taskId, text: m.text, at: Date.now() }));
    if (notificationGranted()) {
      try {
        const n = new Notification(TITLES[m.level] ?? "CSP 提醒", { body: m.text, tag: `${m.taskId}-${m.level}` });
        n.onclick = () => {
          window.focus();
          navigate(`/tasks/${m.taskId}`);
          n.close();
        };
      } catch {
        /* 某些浏览器只允许 Service Worker 发通知 */
      }
    }
  });

  // 有未处理的扫码 / 审核横幅就一直响
  const ring = ringKind(banners);
  useEffect(() => {
    if (ring) startRinging(ring);
    else stopRinging();
    return () => stopRinging();
  }, [ring]);

  const enable = useCallback(async () => {
    await unlockAudio();
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      try {
        await Notification.requestPermission();
      } catch {
        /* 忽略 */
      }
    }
    setEnabled(audioUnlocked());
  }, []);

  const value = useMemo(
    () => ({ banners, toasts, openBanner, dismissBanner, toast, conflict, dismissToast, enabled, enable }),
    [banners, toasts, openBanner, dismissBanner, toast, conflict, dismissToast, enabled, enable],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAlerts(): AlertsApi {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAlerts 必须在 AlertsProvider 里用");
  return v;
}
